import * as React from "react";
import type { QaScopeStrategy } from "./types";

export type QaPhase =
  | "idle"
  | "routing"
  | "rewriting"
  | "detecting_fields"
  | "scoping"
  | "retrieving"
  | "analyzing"
  | "reading_batches"
  | "generating"
  | "complete";

export interface QaProgressMetrics {
  phase: QaPhase;
  routingMode: "PROSE" | "LIST" | null;
  detectedFields: string[];
  detectedFieldsReused: boolean;
  documentCount: number;
  analyzedCount: number;
  mapReduce: boolean;
  batchDone: number;
  batchTotal: number;
  scopeStrategy: QaScopeStrategy | null;
  scopeHyde: boolean;
  selectedDocuments: number;
  phaseTimings: Record<string, number>;
}

interface UseQaProgressReturn {
  metrics: QaProgressMetrics;
  phase: QaPhase;
  setPhase: (phase: QaPhase) => void;
  setRoutingMode: (mode: "PROSE" | "LIST") => void;
  setDetectedFields: (fields: string[], reused: boolean) => void;
  setDocumentCount: (count: number) => void;
  addAnalyzedCount: (count: number) => void;
  setMapReduce: (enabled: boolean) => void;
  setBatchProgress: (done: number, total: number) => void;
  setScope: (strategy: QaScopeStrategy | null, hyde: boolean) => void;
  setSelectedDocuments: (count: number) => void;
  reset: () => void;
}

export const useQaProgress = (): UseQaProgressReturn => {
  const [phase, setPhaseState] = React.useState<QaPhase>("idle");
  const [routingMode, setRoutingMode] = React.useState<"PROSE" | "LIST" | null>(null);
  const [detectedFields, setDetectedFieldsState] = React.useState<string[]>([]);
  const [detectedFieldsReused, setDetectedFieldsReused] = React.useState(false);
  const [documentCount, setDocumentCount] = React.useState(0);
  const [analyzedCount, setAnalyzedCount] = React.useState(0);
  const [mapReduce, setMapReduce] = React.useState(false);
  const [batchDone, setBatchDone] = React.useState(0);
  const [batchTotal, setBatchTotal] = React.useState(0);
  const [scopeStrategy, setScopeStrategy] = React.useState<QaScopeStrategy | null>(null);
  const [scopeHyde, setScopeHyde] = React.useState(false);
  const [selectedDocuments, setSelectedDocuments] = React.useState(0);
  const [phaseTimings, setPhaseTimings] = React.useState<Record<string, number>>({});
  const phaseStartTimeRef = React.useRef<number>(Date.now());

  const setPhase = React.useCallback((newPhase: QaPhase) => {
    setPhaseState(prev => {
      if (prev !== "idle" && prev !== "complete") {
        const duration = Date.now() - phaseStartTimeRef.current;
        setPhaseTimings(timings => ({ ...timings, [prev]: duration }));
      }
      phaseStartTimeRef.current = Date.now();
      return newPhase;
    });
  }, []);

  const setDetectedFields = React.useCallback((fields: string[], reused: boolean) => {
    setDetectedFieldsState(fields);
    setDetectedFieldsReused(reused);
  }, []);

  const addAnalyzedCount = React.useCallback((count: number) => {
    setAnalyzedCount(prev => prev + count);
  }, []);

  const setBatchProgress = React.useCallback((done: number, total: number) => {
    setBatchDone(done);
    setBatchTotal(total);
  }, []);

  const setScope = React.useCallback((strategy: QaScopeStrategy | null, hyde: boolean) => {
    setScopeStrategy(strategy);
    setScopeHyde(hyde);
  }, []);

  const reset = React.useCallback(() => {
    setPhaseState("idle");
    setRoutingMode(null);
    setDetectedFieldsState([]);
    setDetectedFieldsReused(false);
    setDocumentCount(0);
    setAnalyzedCount(0);
    setMapReduce(false);
    setBatchDone(0);
    setBatchTotal(0);
    setScopeStrategy(null);
    setScopeHyde(false);
    setSelectedDocuments(0);
    setPhaseTimings({});
    phaseStartTimeRef.current = Date.now();
  }, []);

  const metrics: QaProgressMetrics = React.useMemo(
    () => ({
      phase,
      routingMode,
      detectedFields,
      detectedFieldsReused,
      documentCount,
      analyzedCount,
      mapReduce,
      batchDone,
      batchTotal,
      scopeStrategy,
      scopeHyde,
      selectedDocuments,
      phaseTimings,
    }),
    [
      phase,
      routingMode,
      detectedFields,
      detectedFieldsReused,
      documentCount,
      analyzedCount,
      mapReduce,
      batchDone,
      batchTotal,
      scopeStrategy,
      scopeHyde,
      selectedDocuments,
      phaseTimings,
    ],
  );

  return {
    metrics,
    phase,
    setPhase,
    setRoutingMode,
    setDetectedFields,
    setDocumentCount,
    addAnalyzedCount,
    setMapReduce,
    setBatchProgress,
    setScope,
    setSelectedDocuments,
    reset,
  };
};
